import { fetchPalette, type PaletteItem } from './handlersRepository';
import { schemaToCoarse, schemaToTypeExpr, type Coarse, type TypeExpr } from '@flows/utils/types';

// Treat undefined/null or an empty object as an unknown schema
function isEmptySchema(s: unknown): boolean {
  if (s == null) return true;
  if (typeof s === 'object') return Object.keys(s as any).length === 0;
  return false;
}

export type Port = { id: string; type: Coarse; typeExpr?: TypeExpr };
// Ops can be strings (simple) or objects with parameters (e.g., tupleAt)
export type LinkOpObject = { name: string; [k: string]: any };
export type LinkOp = string | LinkOpObject;
export type FromRef =
  | { io: 'in' | 'out'; id: string; op?: LinkOp | LinkOp[] }
  | { attr: string; op?: LinkOp | LinkOp[] };
export type ToRef = { io: 'in' | 'out'; id: string };
export type TypeLink =
  | { from: FromRef; to: ToRef }
  | { multiFrom: FromRef[]; to: ToRef; op: 'prefer' };
export type NodeTemplate = {
  key: string;
  title: string;
  category: string;
  icon?: string;
  description?: string;
  ports: { exec: { in: Port[]; out: Port[] }; data: { in: Port[]; out: Port[] } };
  attrsSchema?: unknown;
  inputSchema?: unknown;
  outputSchema?: unknown;
  // hierarchical + search metadata
  pkg: string;
  starter?: boolean;
  synonyms?: string[];
  /** Backend-provided persisted node type (e.g., "parallel"). Used when saving. */
  nodeType?: string;
  /** Backend-provided handler version (e.g., "1.0.0"). */
  version?: string;
  /** Backend-provided per-attribute UI hints. */
  attrsUi?: Record<string, any>;
  /** Backend-provided form layout hints (tabs/sections). */
  formUi?: { tabs?: Array<{ id: string; label: string; fields: string[] }>; sections?: Array<{ id: string; label?: string; fields: string[] }> };
  /** Optional node-local type links for inference (e.g., elementOf(in:items) -> out:value) */
  typeLinks?: TypeLink[];
};

let memo: Map<string, NodeTemplate> | null = null;

export async function loadRegistry(): Promise<Map<string, NodeTemplate>> {
  if (memo) return memo;
  const { items } = await fetchPalette();
  const map = new Map<string, NodeTemplate>();
  for (const raw of items as any[]) {
    const it: any = raw;
    const key: string = it.key || it.handler || it.title || `item-${Math.random().toString(36).slice(2)}`;
    // infer package name from key; fallback to category
    const pkg = (String(key).includes('/') ? String(key).split('/')[0] : String(it.category ?? 'misc')).toLowerCase();
    // derive data pins
    let pIn: Port[] = [];
    let pOut: Port[] = [];
    if (it.ports?.data) {
      // Newer shape: explicit port objects with optional schemas
      pIn = (it.ports.data.in ?? []).map((p: any) => ({ id: String(p.id), type: schemaToCoarse(p.schema), typeExpr: schemaToTypeExpr(p.schema) }));
      pOut = (it.ports.data.out ?? []).map((p: any) => ({ id: String(p.id), type: schemaToCoarse(p.schema), typeExpr: schemaToTypeExpr(p.schema) }));
    } else if (it.pins?.data) {
      // Backend provided data pins under pins.data with either strings or { id }
      const inProps = (it.input && (it.input as any).properties) || {};
      const outProps = (it.output && (it.output as any).properties) || {};
      const toIdArray = (arr: any[] | undefined) =>
        (arr ?? []).map((v: any) => (typeof v === 'string' ? v : v?.id)).filter(Boolean) as string[];
      const inNames: string[] = toIdArray((it.pins as any).data.in);
      const outNames: string[] = toIdArray((it.pins as any).data.out);
      pIn = inNames.map((name) => ({ id: name, type: schemaToCoarse((inProps as any)[name]), typeExpr: schemaToTypeExpr((inProps as any)[name]) }));
      pOut = outNames.map((name) => ({ id: name, type: schemaToCoarse((outProps as any)[name]), typeExpr: schemaToTypeExpr((outProps as any)[name]) }));
    } else if (it.pins) {
      // Legacy shape: pins.in / pins.out as data pins
      const inNames: string[] = Array.isArray((it as any).pins.in) ? (it as any).pins.in : [];
      const outNames: string[] = Array.isArray((it as any).pins.out) ? (it as any).pins.out : [];
      const inProps = (it.input && (it.input as any).properties) || {};
      const outProps = (it.output && (it.output as any).properties) || {};
      pIn = inNames.map((name) => ({ id: name, type: schemaToCoarse((inProps as any)[name]), typeExpr: schemaToTypeExpr((inProps as any)[name]) }));
      pOut = outNames.map((name) => ({ id: name, type: schemaToCoarse((outProps as any)[name]), typeExpr: schemaToTypeExpr((outProps as any)[name]) }));
    }

    // Start with backend-provided type links if present; else accumulate heuristics
    // Heuristic: if exactly one array-typed input and one unknown output (undefined or empty schema), link as element type
    const typeLinks: TypeLink[] = [];
    try {
      const outProps = (it.output && (it.output as any).properties) || {};
      const arrayInputs = pIn.filter((p) => p.typeExpr?.tag === 'array');
      const unknownOuts = (pOut ?? []).filter((p) => {
        const sch = (outProps as any)[p.id];
        return isEmptySchema(sch);
      });
      if (arrayInputs.length === 1 && unknownOuts.length === 1) {
        // Default: elementOf(array) → value
        typeLinks.push({ from: { io: 'in', id: arrayInputs[0]!.id, op: 'element' }, to: { io: 'out', id: unknownOuts[0]!.id } });
      }
    } catch {}

    // derive exec pins — prefer explicit counts from server shape; fall back to sensible defaults
    // Newer server payloads may include `exec: { in: number, out: number }` and/or `startOnly: true`
    const isTriggerCategory = String(it.category ?? '').toLowerCase() === 'trigger';
    const startOnly: boolean = Boolean(it.startOnly) || isTriggerCategory;
    const execInCount: number = (it.exec && typeof it.exec.in === 'number')
      ? Math.max(0, it.exec.in as number)
      : (startOnly ? 0 : 1);
    const execOutCount: number = (it.exec && typeof it.exec.out === 'number')
      ? Math.max(0, it.exec.out as number)
      : 1;

    // Prefer explicit exec pins from backend when available
    let execInPorts: Port[] | null = null;
    let execOutPorts: Port[] | null = null;
    if (it.ports?.exec) {
      execInPorts = (it.ports.exec.in ?? []).map((p: any, i: number) => ({ id: String(p?.id ?? (i === 0 ? 'in' : `in${i + 1}`)), type: 'any' as const }));
      execOutPorts = (it.ports.exec.out ?? []).map((p: any, i: number) => ({ id: String(p?.id ?? (i === 0 ? 'next' : `next${i + 1}`)), type: 'any' as const }));
    } else if ((it as any)?.pins?.exec) {
      const toIdArray = (arr: any[] | undefined, fallbackPrefix: 'in' | 'next') =>
        (arr ?? []).map((v: any, i: number) => String(typeof v === 'string' ? v : v?.id ?? (i === 0 ? (fallbackPrefix === 'in' ? 'in' : 'next') : `${fallbackPrefix}${i + 1}`)));
      const inNames: string[] = toIdArray(((it as any).pins.exec as any).in, 'in');
      const outNames: string[] = toIdArray(((it as any).pins.exec as any).out, 'next');
      execInPorts = inNames.map((id) => ({ id, type: 'any' as const }));
      execOutPorts = outNames.map((id) => ({ id, type: 'any' as const }));
    } else if ((it as any)?.execLabels?.out && Array.isArray((it as any).execLabels.out)) {
      // Fallback: label hints for exec out
      const labels: string[] = (it as any).execLabels.out as string[];
      execInPorts = Array.from({ length: execInCount }, (_, i) => ({ id: i === 0 ? 'in' : `in${i + 1}` as const, type: 'any' as const }));
      execOutPorts = labels.length
        ? labels.map((id) => ({ id: String(id), type: 'any' as const }))
        : Array.from({ length: execOutCount }, (_, i) => ({ id: i === 0 ? 'next' : `next${i + 1}`, type: 'any' as const }));
    }

    // Final fallback if no explicit pins provided
    if (!execInPorts) execInPorts = Array.from({ length: execInCount }, (_, i) => ({ id: i === 0 ? 'in' : `in${i + 1}`, type: 'any' as const }));
    if (!execOutPorts) execOutPorts = Array.from({ length: execOutCount }, (_, i) => ({ id: i === 0 ? 'next' : `next${i + 1}`, type: 'any' as const }));

    // derive x-ui fallback if attrsUi not present
    const attrsSchema = it.attrsSchema ?? it.attrs;
    let xUiFromSchema: Record<string, any> | undefined;
    try {
      const props = (attrsSchema && (attrsSchema as any).properties) || {};
      const entries = Object.entries(props as Record<string, any>)
        .map(([k, v]) => [k, (v && (v as any)['x-ui']) || undefined])
        .filter(([, v]) => v) as Array<[string, any]>;
      if (entries.length) xUiFromSchema = Object.fromEntries(entries);
    } catch {}

    const providedLinks: TypeLink[] | undefined = Array.isArray((it as any)?.typeLinks) ? ((it as any).typeLinks as any) : undefined;
    map.set(key, {
      key,
      title: String(it.title ?? key),
      category: String(it.category ?? 'misc'),
      icon: it.icon,
      description: typeof it.description === 'string' ? it.description : undefined,
      ports: {
        exec: { in: execInPorts, out: execOutPorts },
        data: { in: pIn, out: pOut },
      },
      attrsSchema,
      inputSchema: it.input,
      outputSchema: it.output,
      pkg,
      starter: Boolean((it as any)?.starter),
      synonyms: Array.isArray((it as any)?.synonyms) ? (it as any).synonyms : undefined,
      nodeType: typeof (it as any)?.type === 'string' ? String((it as any).type) : undefined,
      version: typeof (it as any)?.version === 'string' ? String((it as any).version) : undefined,
      attrsUi: (it as any)?.attrsUi ?? xUiFromSchema,
      formUi: (it as any)?.formUi,
      typeLinks: (providedLinks && providedLinks.length ? providedLinks : (typeLinks.length ? typeLinks : undefined)) as any,
    });
  }
  // --- Local patching: enable "as const" for primitive templates without backend changes ---
  const patchPrimitiveTpl = (tpl: NodeTemplate) => {
    // Inject type link to derive type from literal value (attr:value) when asConst is true
    const links = tpl.typeLinks ? [...tpl.typeLinks] : [];
    links.push({ from: { attr: 'value', op: 'literalType' }, to: { io: 'out', id: 'value' } } as any);
    (tpl as any).typeLinks = links;
    // Inject UI toggle for asConst with default true
    const base = (tpl.attrsSchema && typeof tpl.attrsSchema === 'object') ? (tpl.attrsSchema as any) : { type: 'object', properties: {} };
    base.properties = base.properties || {};
    const next = base.properties.asConst ?? { type: 'boolean', title: 'As const', 'x-ui': { widget: 'switch' } };
    base.properties.asConst = { ...next, default: true };
    (tpl as any).attrsSchema = base;
  };
  for (const [, tpl] of map) {
    const key = tpl.key || '';
    const cat = String(tpl.category || '').toLowerCase();
    const pkg = String(tpl.pkg || '').toLowerCase();
    const looksPrimitive = key.startsWith('primitives.') || pkg === 'primitives' || cat.includes('primitive');
    const hasValueOut = !!tpl.ports?.data?.out?.some((p) => p.id === 'value');
    if (looksPrimitive && hasValueOut) {
      patchPrimitiveTpl(tpl);
    }
    // Note: Do not override server-provided links for core.object.get here.
    // When the backend omits links, our heuristic above will add shapePath + prefer.
  }
  // Note: no special-casing for core.array.at here. Expect backend to provide index-aware typing.
  memo = map;
  return map;
}

export function getTemplateByKey(key: string): NodeTemplate | undefined {
  return memo?.get(key);
}
